import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, ArrowUp, ArrowDown, Eye, History, RotateCcw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { isStaffOrAbove } from '../lib/tier';
import { Button } from '../components/ui/button';
import PageTitle from '../components/PageTitle';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import ConfirmDialog from '../components/ConfirmDialog';
import SpellCheckDialog from '../components/SpellCheckDialog';
import { TIP_ICON_SLUGS } from './Help';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HELP_FIELDS = [
  { key: 'hero_subtitle', label: 'Hero subtitle', type: 'textarea', rows: 2, hint: 'Shown under the page title.' },
  { key: 'tips_heading', label: '"Setup tips" section heading', type: 'input' },
  { key: 'tips', label: 'Setup tips', type: 'list', itemSchema: 'tip' },
  { key: 'faq_heading', label: '"FAQ" section heading', type: 'input' },
  { key: 'faqs', label: 'FAQ entries', type: 'list', itemSchema: 'faq' },
  { key: 'community_heading', label: 'Community section heading', type: 'input' },
  { key: 'community_intro', label: 'Community intro paragraph', type: 'textarea', rows: 4 },
  { key: 'community_footnote', label: 'Community footnote (small print)', type: 'textarea', rows: 2 },
];

const ABOUT_FIELDS = [
  { key: 'hero_subtitle', label: 'Hero subtitle', type: 'textarea', rows: 2 },
  { key: 'mission_heading', label: 'Mission heading', type: 'input' },
  { key: 'mission_paragraphs', label: 'Mission paragraphs', type: 'paragraphs', hint: 'One paragraph per item — markdown supported.' },
  { key: 'origin_heading', label: '"Our Beginnings" heading', type: 'input' },
  { key: 'origin_story', label: 'Our Beginnings — origin story', type: 'textarea', rows: 10, hint: 'Brief origin story. Markdown supported. Leave empty to hide this section on the public page.' },
  { key: 'security_heading', label: '"Your data, kept safe" heading', type: 'input' },
  { key: 'security_intro', label: 'Security intro paragraph', type: 'textarea', rows: 3, hint: 'Shown directly under the security heading.' },
  { key: 'security_pillars', label: 'Security pillars', type: 'list', itemSchema: 'terms',
    hint: 'Each pillar has a heading and body. Markdown supported in the body (e.g. **bcrypt** for bold).' },
  { key: 'security_footnote', label: 'Security footnote (italic small print)', type: 'textarea', rows: 3,
    hint: 'The italic line at the end of the security section. Include the contact email or alternative routes if you change them.' },
  { key: 'disclaimer_heading', label: 'Disclaimer heading', type: 'input' },
  { key: 'disclaimer_intro', label: 'Disclaimer intro', type: 'textarea', rows: 4 },
  { key: 'disclaimer_fineprint', label: 'Disclaimer fine print', type: 'textarea', rows: 6 },
  { key: 'invite_heading', label: 'Invite section heading', type: 'input' },
  { key: 'invite_intro', label: 'Invite intro', type: 'textarea', rows: 3 },
  { key: 'invite_footnote', label: 'Invite footnote', type: 'textarea', rows: 2 },
  { key: 'community_heading', label: 'Community heading', type: 'input' },
  { key: 'community_intro', label: 'Community intro', type: 'textarea', rows: 4 },
  { key: 'terms_heading', label: 'Terms heading', type: 'input' },
  { key: 'terms_intro', label: 'Terms intro / disclaimer line', type: 'textarea', rows: 3 },
  { key: 'terms_sections', label: 'Terms sections', type: 'list', itemSchema: 'terms' },
];

const PRICING_FIELDS = [
  // Tier names and prices are intentionally not editable — they're
  // revenue-critical and flow into Stripe + the founder-discount logic.
  // Only the bullet points beneath each price are CMS-controlled.
  { key: 'ember_features', label: 'Ember bullet points', type: 'paragraphs',
    hint: 'One bullet per item. Markdown supported (e.g. **Open Banking** for bold).' },
  { key: 'steel_features', label: 'Steel bullet points', type: 'paragraphs',
    hint: 'One bullet per item. Markdown supported.' },
  { key: 'damascus_features', label: 'Damascus bullet points', type: 'paragraphs',
    hint: 'One bullet per item. Markdown supported.' },
  { key: 'titanium_features', label: 'Titanium bullet points', type: 'paragraphs',
    hint: 'One bullet per item. Markdown supported.' },
];

const SLUG_TO_FIELDS = { help: HELP_FIELDS, about: ABOUT_FIELDS, pricing: PRICING_FIELDS };
const SLUG_TO_PREVIEW = { help: '/help', about: '/about', pricing: '/pricing' };
const SLUG_TO_TITLE = { help: 'Help & FAQ', about: 'About Us' };

const ITEM_SCHEMAS = {
  tip: { defaults: { icon: 'sparkles', title: '', body: '' } },
  faq: { defaults: { q: '', a: '' } },
  terms: { defaults: { heading: '', body: '' } },
};

const formatRelative = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hr ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const MarkdownHelp = () => (
  <div className="text-[11px] text-[#64748B] bg-[#F8FAFC] border border-[#E2E8F0] rounded px-2 py-1.5">
    Markdown: <code className="font-mono">**bold**</code>, <code className="font-mono">*italic*</code>, <code className="font-mono">[text](https://url)</code>. Blank lines create paragraphs.
  </div>
);

const ListItemEditor = ({ schema, index, item, onChange, onMove, onRemove, total }) => {
  const setField = (k, v) => onChange({ ...item, [k]: v });
  return (
    <div className="border border-[#E2E8F0] rounded-md p-4 bg-[#FAFBFC] space-y-3" data-testid={`list-item-${schema}-${index}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#64748B]">#{index + 1}</span>
        <div className="flex items-center gap-1">
          <button type="button" onClick={() => onMove(index, -1)} disabled={index === 0} className="p-1 rounded hover:bg-[#E2E8F0] disabled:opacity-30" title="Move up" data-testid={`list-up-${schema}-${index}`}>
            <ArrowUp className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => onMove(index, 1)} disabled={index === total - 1} className="p-1 rounded hover:bg-[#E2E8F0] disabled:opacity-30" title="Move down" data-testid={`list-down-${schema}-${index}`}>
            <ArrowDown className="w-3.5 h-3.5" />
          </button>
          <button type="button" onClick={() => onRemove(index)} className="p-1 rounded hover:bg-[#FEE2E2] text-[#EF4444]" title="Delete" data-testid={`list-remove-${schema}-${index}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {schema === 'tip' && (
        <>
          <div>
            <Label className="text-xs">Icon</Label>
            <Select value={item.icon || 'sparkles'} onValueChange={(v) => setField('icon', v)}>
              <SelectTrigger className="mt-1" data-testid={`tip-icon-${index}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIP_ICON_SLUGS.map((slug) => (
                  <SelectItem key={slug} value={slug}>{slug}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Title</Label>
            <Input value={item.title || ''} onChange={(e) => setField('title', e.target.value)} className="mt-1" data-testid={`tip-title-${index}`} />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea value={item.body || ''} onChange={(e) => setField('body', e.target.value)} rows={4} className="mt-1" data-testid={`tip-body-${index}`} />
          </div>
        </>
      )}

      {schema === 'faq' && (
        <>
          <div>
            <Label className="text-xs">Question</Label>
            <Input value={item.q || ''} onChange={(e) => setField('q', e.target.value)} className="mt-1" data-testid={`faq-q-${index}`} />
          </div>
          <div>
            <Label className="text-xs">Answer</Label>
            <Textarea value={item.a || ''} onChange={(e) => setField('a', e.target.value)} rows={5} className="mt-1" data-testid={`faq-a-${index}`} />
          </div>
        </>
      )}

      {schema === 'terms' && (
        <>
          <div>
            <Label className="text-xs">Heading</Label>
            <Input value={item.heading || ''} onChange={(e) => setField('heading', e.target.value)} className="mt-1" data-testid={`terms-heading-${index}`} />
          </div>
          <div>
            <Label className="text-xs">Body</Label>
            <Textarea value={item.body || ''} onChange={(e) => setField('body', e.target.value)} rows={5} className="mt-1" data-testid={`terms-body-${index}`} />
          </div>
        </>
      )}
    </div>
  );
};

const EditPageContent = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [spellCheckFindings, setSpellCheckFindings] = useState(null); // null | [findings]
  const [spellCheckBusy, setSpellCheckBusy] = useState(false);

  const fields = useMemo(() => SLUG_TO_FIELDS[slug] || [], [slug]);
  const isStaff = isStaffOrAbove(user);

  // Each list item carries its own `_uid` so it survives reorder/edit
  // without the index-key bug (focus/scroll mis-attached to wrong row).
  // Paragraphs are plain strings (max ~5 items in mission_paragraphs) and
  // use a position+length composite key; the user-visible impact of the
  // index-key bug on a 5-item string list is negligible.
  const counterRef = useRef(0);
  const newUid = () => {
    counterRef.current += 1;
    return `lk-${counterRef.current}`;
  };
  /** On load/restore/reset, attach a _uid to every list-item that doesn't
   *  already have one (e.g. items just hydrated from the API). */
  const replaceData = useCallback((newData) => {
    if (!newData) { setData(newData); return; }
    const seeded = { ...newData };
    fields.forEach((f) => {
      if (f.type !== 'list') return;
      const list = seeded[f.key];
      if (!Array.isArray(list)) return;
      seeded[f.key] = list.map((item) => (item._uid ? item : { ...item, _uid: newUid() }));
    });
    setData(seeded);
  }, [fields]);

  const seedUids = (newData) => {
    if (!newData) return newData;
    const seeded = { ...newData };
    fields.forEach((f) => {
      if (f.type !== 'list') return;
      const list = seeded[f.key];
      if (!Array.isArray(list)) return;
      seeded[f.key] = list.map((item) => (item._uid ? item : { ...item, _uid: newUid() }));
    });
    return seeded;
  };

  const fetchHistory = async () => {
    try {
      const r = await axios.get(`${API}/page-content/${slug}/history`, { withCredentials: true });
      setHistory(r.data || []);
    } catch {
      setHistory([]);
    }
  };

  useEffect(() => {
    if (!fields.length) return undefined;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/page-content/${slug}`, { withCredentials: true });
        if (!cancelled) replaceData(r.data);
      } catch {
        if (!cancelled) toast.error('Failed to load page content');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [slug, fields.length, replaceData]);

  if (!fields.length) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="edit-page-unknown">
        <p className="text-[#EF4444]">Unknown page: {slug}</p>
      </div>
    );
  }

  if (authLoading || loading) {
    return <div className="p-8 text-sm text-[#64748B]" data-testid="edit-page-loading">Loading…</div>;
  }

  if (!isStaff) {
    return (
      <div className="p-8 max-w-3xl mx-auto" data-testid="edit-page-forbidden">
        <p className="text-[#EF4444]">Staff only. Log in with an admin account to edit page content.</p>
      </div>
    );
  }

  const setField = (key, value) => setData((d) => ({ ...d, [key]: value }));

  const handleListChange = (key, index, newItem) => {
    const list = [...(data[key] || [])];
    list[index] = newItem;
    setField(key, list);
  };
  const handleListMove = (key, index, delta) => {
    const list = [...(data[key] || [])];
    const target = index + delta;
    if (target < 0 || target >= list.length) return;
    [list[index], list[target]] = [list[target], list[index]];
    setField(key, list);
  };
  const handleListRemove = (key, index) => {
    const list = [...(data[key] || [])];
    list.splice(index, 1);
    setField(key, list);
  };
  const handleListAdd = (key, schema) => {
    const list = [...(data[key] || [])];
    list.push({ ...ITEM_SCHEMAS[schema].defaults, _uid: newUid() });
    setField(key, list);
  };

  const handleParagraphsChange = (key, index, value) => {
    const list = [...(data[key] || [])];
    list[index] = value;
    setField(key, list);
  };
  const handleParagraphsMove = (key, index, delta) => {
    const list = [...(data[key] || [])];
    const t = index + delta;
    if (t < 0 || t >= list.length) return;
    [list[index], list[t]] = [list[t], list[index]];
    setField(key, list);
  };
  const handleParagraphsRemove = (key, index) => {
    const list = [...(data[key] || [])];
    list.splice(index, 1);
    setField(key, list);
  };
  const handleParagraphsAdd = (key) => {
    setField(key, [...(data[key] || []), '']);
  };

  // Build the cleaned save payload (strip client-only _uid markers).
  const buildPayload = () => {
    const payload = { ...data };
    fields.forEach((f) => {
      if (f.type === 'list' && Array.isArray(payload[f.key])) {
        payload[f.key] = payload[f.key].map(({ _uid, ...rest }) => rest);
      }
    });
    return payload;
  };

  // Actually persist a fully-prepared payload. Used by `save()` (after
  // an OK spell-check) and by the SpellCheckDialog accept handlers.
  const persistPayload = async (payload) => {
    setSaving(true);
    try {
      await axios.put(`${API}/page-content/${slug}`, payload, { withCredentials: true });
      toast.success('Saved');
      if (historyOpen) fetchHistory();
      replaceData(payload); // mirror so the editor reflects any applied UK fixes
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to save';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const save = async () => {
    setSaving(true);
    const payload = buildPayload();
    // Run the UK English check first. Any findings → open the diff modal.
    try {
      const r = await axios.post(
        `${API}/admin/spell-check`,
        { payload },
        { withCredentials: true },
      );
      if (r.data?.total_issues > 0) {
        setSpellCheckFindings(r.data.findings);
        setSaving(false);
        return; // wait for the admin to pick "Apply all" or "Save as-is"
      }
    } catch (err) {
      // Spell-check is best-effort; if it fails (e.g. dev preview), fall
      // through to the normal save rather than blocking the user.
      console.warn('Spell-check failed, saving without it:', err);
    }
    await persistPayload(payload);
  };

  const applySpellFixesAndSave = async () => {
    setSpellCheckBusy(true);
    try {
      const r = await axios.post(
        `${API}/admin/spell-check/apply`,
        { payload: buildPayload() },
        { withCredentials: true },
      );
      const cleaned = r.data?.payload;
      setSpellCheckFindings(null);
      await persistPayload(cleaned);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not apply UK fixes');
    } finally {
      setSpellCheckBusy(false);
    }
  };

  const saveAsIs = async () => {
    setSpellCheckBusy(true);
    try {
      setSpellCheckFindings(null);
      await persistPayload(buildPayload());
    } finally {
      setSpellCheckBusy(false);
    }
  };

  const handleRestore = async (versionId, isDefaults) => {
    try {
      const r = await axios.post(`${API}/page-content/${slug}/restore/${versionId}`, {}, { withCredentials: true });
      replaceData(r.data);
      toast.success(isDefaults ? 'Restored to a defaults snapshot' : 'Version restored — review and Save to confirm');
      setHistoryOpen(false);
      fetchHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to restore';
      toast.error(msg);
    }
  };

  const handleResetDefaults = async () => {
    try {
      const r = await axios.post(`${API}/page-content/${slug}/reset`, {}, { withCredentials: true });
      replaceData(r.data);
      toast.success('Reverted to baked-in defaults');
      setResetConfirmOpen(false);
      fetchHistory();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Failed to reset';
      toast.error(msg);
    }
  };

  return (
    <div className="p-6 sm:p-8 max-w-3xl mx-auto" data-testid={`edit-page-${slug}`}>
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-sm text-[#0052FF] inline-flex items-center gap-1 hover:underline" data-testid="edit-page-back">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-8">
        <PageTitle
          title={SLUG_TO_TITLE[slug]}
          subtitle="Changes are saved instantly to the live page. Reload the public page to see them."
          className="mb-0"
        />
        <div className="flex gap-2 flex-wrap">
          <Popover open={historyOpen} onOpenChange={(open) => { setHistoryOpen(open); if (open) fetchHistory(); }}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="text-sm" data-testid="edit-page-history-trigger">
                <History className="w-4 h-4 mr-1.5" /> History
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[340px] p-0" align="end">
              <div className="px-4 py-3 border-b border-[#E2E8F0]">
                <div className="text-sm font-semibold text-[#020617]">Version history</div>
                <div className="text-xs text-[#64748B] mt-0.5">Last 5 saves. Restoring snapshots the current state first so you can always undo.</div>
              </div>
              {history.length === 0 ? (
                <div className="px-4 py-6 text-sm text-[#64748B] text-center" data-testid="edit-page-history-empty">
                  No prior versions yet.
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto divide-y divide-[#E2E8F0]">
                  {history.map((v) => (
                    <div key={v.id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid={`edit-page-history-row-${v.id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-[#020617]">{formatRelative(v.saved_at)}</div>
                        <div className="text-[11px] text-[#64748B] mt-0.5">
                          {v.label === 'reset-to-defaults' && 'Snapshot before resetting to defaults'}
                          {v.label === 'restore' && 'Snapshot before restoring an older version'}
                          {v.label === 'edit' && 'Snapshot before an edit'}
                          {v.is_defaults && ' · defaults state'}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7 px-2"
                        onClick={() => handleRestore(v.id, v.is_defaults)}
                        data-testid={`edit-page-history-restore-${v.id}`}
                      >
                        Restore
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </PopoverContent>
          </Popover>

          <Button
            variant="outline"
            className="text-sm text-[#B45309] border-[#FCD34D] hover:bg-[#FEF3C7]"
            onClick={() => setResetConfirmOpen(true)}
            data-testid="edit-page-reset-defaults"
          >
            <RotateCcw className="w-4 h-4 mr-1.5" /> Reset to defaults
          </Button>

          <Link to={SLUG_TO_PREVIEW[slug]} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#E2E8F0] text-sm font-medium text-[#334155] hover:border-[#0052FF] hover:text-[#0052FF]" data-testid="edit-page-preview">
            <Eye className="w-4 h-4" /> Preview
          </Link>
          <Button onClick={save} disabled={saving} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="edit-page-save">
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </div>

      <MarkdownHelp />

      <div className="mt-6 space-y-6">
        {fields.map((f) => (
          <div key={f.key} className="bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid={`field-${f.key}`}>
            <Label className="text-sm font-semibold text-[#020617]">{f.label}</Label>
            {f.hint && <p className="text-[11px] text-[#94A3B8] mt-0.5">{f.hint}</p>}

            {f.type === 'input' && (
              <Input
                value={data?.[f.key] || ''}
                onChange={(e) => setField(f.key, e.target.value)}
                className="mt-2"
                data-testid={`field-input-${f.key}`}
              />
            )}

            {f.type === 'textarea' && (
              <Textarea
                value={data?.[f.key] || ''}
                onChange={(e) => setField(f.key, e.target.value)}
                rows={f.rows || 3}
                className="mt-2"
                data-testid={`field-textarea-${f.key}`}
              />
            )}

            {f.type === 'paragraphs' && (
              <div className="mt-2 space-y-3">
                {(data?.[f.key] || []).map((p, i) => (
                  <div key={`${f.key}-${i}-${(data[f.key] || []).length}`} className="border border-[#E2E8F0] rounded-md p-3 bg-[#FAFBFC]">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#64748B]">¶ {i + 1}</span>
                      <div className="flex items-center gap-1">
                        <button type="button" onClick={() => handleParagraphsMove(f.key, i, -1)} disabled={i === 0} className="p-1 rounded hover:bg-[#E2E8F0] disabled:opacity-30">
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => handleParagraphsMove(f.key, i, 1)} disabled={i === (data[f.key] || []).length - 1} className="p-1 rounded hover:bg-[#E2E8F0] disabled:opacity-30">
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button type="button" onClick={() => handleParagraphsRemove(f.key, i)} className="p-1 rounded hover:bg-[#FEE2E2] text-[#EF4444]">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    <Textarea
                      value={p}
                      onChange={(e) => handleParagraphsChange(f.key, i, e.target.value)}
                      rows={3}
                      data-testid={`paragraph-${f.key}-${i}`}
                    />
                  </div>
                ))}
                <Button type="button" onClick={() => handleParagraphsAdd(f.key)} variant="outline" size="sm" data-testid={`paragraph-add-${f.key}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add paragraph
                </Button>
              </div>
            )}

            {f.type === 'list' && (
              <div className="mt-2 space-y-3">
                {(data?.[f.key] || []).map((item, i) => (
                  <ListItemEditor
                    key={item._uid || `fallback-${f.key}-${i}`}
                    schema={f.itemSchema}
                    index={i}
                    item={item}
                    total={(data[f.key] || []).length}
                    onChange={(newItem) => handleListChange(f.key, i, newItem)}
                    onMove={(idx, delta) => handleListMove(f.key, idx, delta)}
                    onRemove={(idx) => handleListRemove(f.key, idx)}
                  />
                ))}
                <Button type="button" onClick={() => handleListAdd(f.key, f.itemSchema)} variant="outline" size="sm" data-testid={`list-add-${f.key}`}>
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add {f.itemSchema}
                </Button>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="mt-8 flex gap-2 justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0052FF] text-white hover:bg-[#003ECC]" data-testid="edit-page-save-bottom">
          <Save className="w-4 h-4 mr-1.5" />
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </div>

      <ConfirmDialog
        open={resetConfirmOpen}
        onOpenChange={setResetConfirmOpen}
        title="Reset to baked-in defaults?"
        description={`This will wipe the live ${SLUG_TO_TITLE[slug]} page back to the wording that ships in code. The current version will be snapshotted into history first, so you can restore it from the History popover if you change your mind.`}
        confirmLabel="Reset"
        onConfirm={handleResetDefaults}
        testId="edit-page-reset-confirm"
      />

      <SpellCheckDialog
        open={!!spellCheckFindings}
        onOpenChange={(open) => { if (!open) setSpellCheckFindings(null); }}
        findings={spellCheckFindings || []}
        onApplyAll={applySpellFixesAndSave}
        onSaveAsIs={saveAsIs}
        busy={spellCheckBusy}
      />
    </div>
  );
};

export default EditPageContent;
